import WarpElement from "@warp-ds/elements-core";
import { css, html } from "lit";
import { CategoryWithLink } from "../../models/category.js";
import { Brand, Subvertical } from "../../constants.js";
import { TrackingEventMap } from "../../models/trackingEvent.js";
import { largeNumberFormatter } from "../../utils/formatters.js";
import { getLocaleByBrand } from "../../utils/getLocaleByBrand.js";
import { trackClickEvent } from "../tracking.js";
import { icon, IconName } from "./icon.js";

class Categories extends WarpElement {
    declare imageUrl: string;
    declare alt: string;
    declare objectPosition: string;
    declare title: string;
    declare iconName: IconName;
    declare categories: Array<CategoryWithLink>;
    declare brand: Brand;
    declare subvertical: Subvertical;
    declare trackingEventMap: TrackingEventMap;

    static properties = {
        imageUrl: { type: String },
        alt: { type: String },
        objectPosition: { type: String },
        title: { type: String },
        iconName: { type: String },
        categories: { type: Array },
        brand: { type: String },
        subvertical: { type: String },
        trackingEventMap: { type: Object },
    };

    static styles = [
        WarpElement.styles,
        css`
            .hero-gradient {
                background: linear-gradient(
                    0deg,
                    rgba(0, 0, 0, 0) 0%,
                    rgba(0, 0, 0, 0.32) 32%,
                    rgba(0, 0, 0, 0.52) 52%,
                    rgba(0, 0, 0, 0.74) 74%
                );
            }
            @media (min-width: 768px) {
                .hero-gradient {
                    background: linear-gradient(
                        90deg,
                        rgba(0, 0, 0, 0.55) 0%,
                        rgba(0, 0, 0, 0.32) 25%,
                        rgba(0, 0, 0, 0.12) 55%,
                        rgba(0, 0, 0, 0) 80%
                    );
                }
            }
            .title-shadow {
                text-shadow:
                    0 1px 1px rgba(0, 0, 0, 0.16),
                    0 1px 6px rgba(0, 0, 0, 0.12);
            }
            .scrollbar-hide {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }
            .scrollbar-hide::-webkit-scrollbar {
                display: none;
            }
            /* Mirror the global .page-container shortcut, which cannot reach
               into this component's shadow root. */
            .page-container {
                max-width: 1010px;
                margin: 0;
                padding: 0 16px;
            }
            @media (min-width: 1300px) {
                .page-container {
                    margin-left: auto;
                    margin-right: auto;
                    padding-left: 31px;
                    padding-right: 31px;
                }
            }
            @css-placeholder-MvMPcP;
        `,
    ];

    render() {
        const locale = getLocaleByBrand(this.brand);
        const objectPosition = this.objectPosition || "center 35%";
        const trackingEventMapJson = JSON.stringify(this.trackingEventMap);

        const renderSellButton = html`<mfp-sell-mobility-vehicle
            brand="${this.brand}"
            subvertical="${this.subvertical}"
            trackingEventMap="${trackingEventMapJson}"
        ></mfp-sell-mobility-vehicle>`;

        const categories = this.categories ?? [];
        const [topRow, bottomRow] = [0, 1].map((r) => categories.filter((_, i) => i % 2 === r));

        const renderPill = (category: CategoryWithLink) =>
            html`<li class="shrink-0">
                <a
                    class="grid grid-cols-[24px_auto] items-center gap-x-16 py-4 px-16 rounded-full s-bg-subtle hover:s-bg-hover active:s-bg-active transition-colors hover:no-underline"
                    href="${category.link}"
                    @click=${() => trackClickEvent(category.trackingEvent)}
                >
                    <span class="s-icon-secondary grid place-items-center w-24 h-24">
                        ${icon({ name: category.iconName, size: "medium", locale })}
                    </span>
                    <span class="flex flex-col items-start justify-center min-h-[40px]">
                        <p class="s-text text-caption font-bold mb-0">${category.title}</p>
                        ${
                            category.hits
                                ? html`<p class="s-text-subtle font-light text-caption mb-0">
                                      ${largeNumberFormatter(category.hits, locale)}
                                  </p>`
                                : ""
                        }
                    </span>
                </a>
            </li>`;

        return html`
            <section class="relative page-container">
                <div class="relative w-full h-[330px] md:h-[260px]">
                    <img
                        class="absolute top-0 left-1/2 -translate-x-1/2 w-screen max-w-none h-full object-cover"
                        style="object-position: ${objectPosition};"
                        src="${this.imageUrl}"
                        alt="${this.alt}"
                        loading="eager"
                        decoding="async"
                    />
                    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-screen max-w-none h-full hero-gradient" aria-hidden="true"></div>
                    <div class="absolute top-16 md:top-40 left-0 right-0 z-1">
                        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-16">
                            <div class="flex items-center gap-12 min-w-0">
                                <span class="grid place-items-center w-32 h-32 shrink-0 s-text-inverted-static">
                                    ${icon({ name: this.iconName, size: "large", locale })}
                                </span>
                                <h1 class="t1 md:t1 m-0 s-text-inverted-static title-shadow min-w-0 break-words">${this.title}</h1>
                            </div>
                            <div class="shrink-0">${renderSellButton}</div>
                        </div>
                    </div>

                    <nav class="absolute left-[calc(50%-50vw)] right-[calc(50%-50vw)] bottom-40 md:left-0 md:right-0">
                        <div class="md:hidden overflow-x-auto overflow-y-hidden scrollbar-hide">
                            <ul class="flex w-max gap-8 list-none m-0 p-0 px-16 mb-8">
                                ${topRow.map(renderPill)}
                            </ul>
                            <ul class="flex w-max gap-8 list-none m-0 p-0 px-16">
                                ${bottomRow.map(renderPill)}
                            </ul>
                        </div>
                        <ul class="hidden md:flex md:flex-wrap gap-8 pb-4 list-none m-0">
                            ${categories.map(renderPill)}
                        </ul>
                    </nav>
                </div>
            </section>
        `;
    }
}

customElements.define("category-list", Categories as unknown as CustomElementConstructor);
